import { useState, useEffect, useCallback } from "react";
import { 
  MessageSquare, Loader2, Trash2, Mail, Phone, Calendar, Eye, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import apiClient from "@/utils/api";
import AdminLayout from "@/components/AdminLayout";

const MessagesPage = () => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error("Error al cargar mensajes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este mensaje?")) return;
    
    setDeleting(id);
    try {
      await apiClient.delete(`/contacts/${id}`);
      toast.success("Mensaje eliminado");
      setContacts(contacts.filter(c => c.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (error) {
      toast.error("Error al eliminar el mensaje");
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Mensajes</h1>
          <p className="text-[#A3A3A3] mt-1">Mensajes recibidos del formulario de contacto</p>
        </div>
        <div className="text-[#A3A3A3]">
          {contacts.length} mensaje{contacts.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-lg p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-white/20 mb-4" />
          <h3 className="text-xl text-white mb-2">No hay mensajes</h3>
          <p className="text-[#A3A3A3]">Los mensajes del formulario de contacto aparecerán aquí</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1 bg-[#121212] border border-white/5 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h2 className="text-lg font-medium text-white">Bandeja de entrada</h2>
            </div>
            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
              {contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setSelectedMessage(contact)}
                  className={`w-full p-4 text-left hover:bg-white/5 transition-colors ${
                    selectedMessage?.id === contact.id ? 'bg-[#D4AF37]/10 border-l-2 border-[#D4AF37]' : ''
                  }`}
                  data-testid={`message-item-${contact.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{contact.name}</p>
                      <p className="text-[#A3A3A3] text-sm truncate">{contact.email}</p>
                      <p className="text-[#525252] text-xs mt-1 line-clamp-2">{contact.message}</p>
                    </div>
                  </div>
                  <p className="text-[#525252] text-xs mt-2">{formatDate(contact.created_at)}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/5 rounded-lg overflow-hidden">
            {selectedMessage ? (
              <>
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-white">Detalle del mensaje</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(selectedMessage.id)}
                    disabled={deleting === selectedMessage.id}
                    className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
                  >
                    {deleting === selectedMessage.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-6 space-y-6">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <span className="text-[#D4AF37] font-semibold text-lg">
                          {selectedMessage.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium text-lg">{selectedMessage.name}</p>
                        <p className="text-[#A3A3A3] text-sm">{selectedMessage.interest_type || 'General'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      <a 
                        href={`mailto:${selectedMessage.email}`}
                        className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <Mail className="w-5 h-5 text-[#D4AF37]" />
                        <span className="text-white text-sm">{selectedMessage.email}</span>
                      </a>
                      {selectedMessage.phone && (
                        <a 
                          href={`tel:${selectedMessage.phone}`}
                          className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <Phone className="w-5 h-5 text-[#D4AF37]" />
                          <span className="text-white text-sm">{selectedMessage.phone}</span>
                        </a>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-[#525252] text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>Recibido: {formatDate(selectedMessage.created_at)}</span>
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className="pt-4 border-t border-white/5">
                    <h3 className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-3">Mensaje</h3>
                    <p className="text-white leading-relaxed whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>

                  {/* Property Reference */}
                  {selectedMessage.property_id && (
                    <div className="pt-4 border-t border-white/5">
                      <h3 className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-3">Propiedad de interés</h3>
                      <Link 
                        to={`/admin/propiedades/${selectedMessage.property_id}`}
                        className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
                      >
                        <Eye className="w-4 h-4" />
                        Ver propiedad
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t border-white/5 flex flex-wrap gap-3">
                    <a 
                      href={`mailto:${selectedMessage.email}?subject=Re: Consulta en Ikonic Deals`}
                      className="flex-1"
                    >
                      <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                        <Mail className="w-4 h-4 mr-2" />
                        Responder por Email
                      </Button>
                    </a>
                    {selectedMessage.phone && (
                      <a 
                        href={`https://wa.me/${selectedMessage.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${selectedMessage.name}, gracias por contactar a Ikonic Deals. `)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                          <Phone className="w-4 h-4 mr-2" />
                          WhatsApp
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[500px]">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto text-white/10 mb-4" />
                  <p className="text-[#A3A3A3]">Selecciona un mensaje para ver los detalles</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default MessagesPage;
